import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Activity, Zap, Smartphone, Search, ArrowRight, Check } from 'lucide-react';
import { buildSrc, buildSrcSet, IMAGE_WIDTHS } from '@/lib/responsiveImage';

const CHECK_ITEMS = [
  'Performance-Check',
  'Responsive-Check',
  'SEO-Basisanalyse',
  'KI-Webseitencheck: Sichtbarkeit in KI-Antworten',
  'Prüfung von Impressum & Datenschutzerklärung auf Rechtssicherheit',
  'Besprechung der Ergebnisse',
];

const WebseitencheckTeaser = () => {
  const imageBase = '/images/services/webseitencheck';
  const imageSizes = '(min-width: 1024px) 40vw, 100vw';

  return (
    <section id="webseitencheck" className="py-24 md:py-32 relative section-dark">
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="order-2 lg:order-1"
          >
            <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center mb-6">
              <Activity className="w-8 h-8 text-primary" aria-hidden="true" />
            </div>
            <span className="text-primary font-medium text-sm uppercase tracking-wider">Webseitencheck</span>
            <h2 className="text-3xl md:text-5xl font-display font-bold mt-4 mb-6 text-white">
              Warum Ihre Webseite
              <span className="gradient-text"> professionell prüfen?</span>
            </h2>
            <p className="text-white/80 text-lg mb-8">
              Performance, Responsive Design, SEO und KI-Sichtbarkeit – wir analysieren Ihre Webseite und besprechen die Ergebnisse persönlich mit Ihnen. Transparente Analyse mit konkreten Handlungsempfehlungen.
            </p>

            <ul className="space-y-4 mb-8">
              {CHECK_ITEMS.map((item, index) => (
                <motion.li
                  key={item}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-6 h-6 rounded-full bg-primary/30 flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-primary" aria-hidden="true" />
                  </div>
                  <span className="text-white/90">{item}</span>
                </motion.li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/webseitencheck"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-primary to-glow-secondary text-primary-foreground font-semibold hover-glow shadow-lg transition-all"
              >
                Webseitencheck anfragen
                <ArrowRight className="w-5 h-5" aria-hidden="true" />
              </Link>
              <span className="self-center text-white/90 text-sm">
                Jetzt kostenlos!
              </span>
            </div>

          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="order-1 lg:order-2"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={buildSrc(imageBase, 800)}
                srcSet={buildSrcSet(imageBase, IMAGE_WIDTHS.card)}
                sizes={imageSizes}
                alt="Webseitencheck – Analyse Ihrer Webseite"
                loading="lazy"
                decoding="async"
                width={800}
                height={570}
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/90 backdrop-blur-sm text-white font-semibold">
                  <Zap className="w-4 h-4" aria-hidden="true" />
                  <span>Performance</span>
                  <Smartphone className="w-4 h-4" aria-hidden="true" />
                  <span>Responsive</span>
                  <Search className="w-4 h-4" aria-hidden="true" />
                  <span>SEO</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WebseitencheckTeaser;
